/**
 * Display utils
 */

import format from 'date-fns/format'
import { KPI as PrismaKPI } from '@prisma/client'
import { CurrencyType, KPI } from './features/insights_hub/components/TopPerformingAds/__generated__/TopPerformingAdsQuery.graphql'

export function serializeInsightRate(rate: number, kpi: PrismaKPI, currencyType: CurrencyType): string {
  const kpiStr = kpi as unknown as string
  if (
    kpi === PrismaKPI.COST_PER_PURCHASE ||
    kpi === PrismaKPI.COST_PER_LINK_CLICK ||
    kpi === PrismaKPI.COST_PER_ADD_TO_CART ||
    kpi === PrismaKPI.COST_PER_LANDING_PAGE_VIEW ||
    kpi === PrismaKPI.COST_PER_ENGAGEMENT ||
    kpi === PrismaKPI.COST_PER_TOTAL_PURCHASE ||
    kpi === PrismaKPI.COST_PER_LEAD ||
    kpi === PrismaKPI.COST_PER_STORE_LOCATOR ||
    kpi === PrismaKPI.COST_PER_COMPLETED_REGISTRATION ||
    kpi === PrismaKPI.COST_PER_INITIATED_CHECKOUT ||
    kpi === PrismaKPI.CPM ||
    kpi === PrismaKPI.COST_PER_TRIAL_STARTED ||
    kpiStr === 'COST_PER_INSTALL' ||
    kpiStr === 'COST_PER_DONATION'
  ) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyType,
    }).format(rate)
  } else if (
    kpi === PrismaKPI.PURCHASES_TO_LPV ||
    kpi === PrismaKPI.PURCHASES_TO_IMPRESSIONS ||
    kpi === PrismaKPI.LANDING_PAGE_VIEW_RATE ||
    kpi === PrismaKPI.ENGAGEMENT_RATE ||
    kpi === PrismaKPI.AGGREGATE_CONVERSION_RATE ||
    kpi === PrismaKPI.PURCHASES_TO_LINK_CLICK ||
    kpi === PrismaKPI.LEAD_CONVERSION_RATE ||
    kpi === PrismaKPI.HOOK_RATE ||
    kpi === PrismaKPI.LEAD_RATE ||
    kpi === PrismaKPI.RESULT_RATE ||
    kpi === PrismaKPI.CONVERSION_RATE ||
    kpi === PrismaKPI.TRIALS_STARTED_TO_IMPRESSIONS
  ) {
    return `${(rate * 100).toFixed(2).toLocaleString()}%`
  } else if (
    kpi === PrismaKPI.WEBSITE_PURCHASES ||
    kpi === PrismaKPI.PURCHASES ||
    kpi === PrismaKPI.TRIALS_STARTED ||
    kpi === PrismaKPI.IMPRESSIONS ||
    kpi === PrismaKPI.REACH
  ) {
    return Math.round(rate).toLocaleString()
  } else if (kpi === PrismaKPI.ROAS || kpi === PrismaKPI.TOTAL_ROAS) {
    return `${rate.toFixed(1)}X`
  } else if (kpi === PrismaKPI.CLICK_THROUGH_RATE) {
    return `${(rate * 100).toFixed(2)}%`
  }

  return rate.toLocaleString()
}

export const deltaAsPercent = (delta: number) => (delta * 100).toFixed(1)



export const currencyFormat = (num: number, currencyType: CurrencyType) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyType,
  }).format(num)
}

export function getDollarFormat(currencyType: CurrencyType): Intl.NumberFormat {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyType,
    maximumFractionDigits: 0
  });
}


export const KpiDisplay: Record<string, string> = {
  PURCHASES_TO_LPV: 'Website purchases to LP Views',
  PURCHASES_TO_IMPRESSIONS: 'Website purchases to Impressions',
  WEBSITE_PURCHASES: 'Website purchases',
  COST_PER_LANDING_PAGE_VIEW: 'Cost per LP Views',
  ENGAGEMENT_RATE: "Engagement rate",
  COST_PER_PURCHASE: 'Cost per website purchase',
  COST_PER_INSTALL: 'Cost per install',
  COST_PER_DONATION: 'Cost per donation',
  COST_PER_LINK_CLICK: 'Cost per link click',
  COST_PER_ADD_TO_CART: 'Cost per add to cart',
  COST_PER_ENGAGEMENT: 'Cost per engagement',
  LANDING_PAGE_VIEW_RATE: 'Landing page view rate',
  ROAS: 'Web ROAS',
  CLICK_THROUGH_RATE: 'Link click-through rate',
  '%future added value': '',
  AVG_DAILY_SPEND: 'Average daily spend',
  AVG_DAILY_PURCHASE_VOLUME: 'Average daily purchase volume',
  HOOK_RATE: 'Hook rate (3s)',
  SPEND: 'Spend',
  AGGREGATE_CONVERSION_RATE: 'Aggregate conversion rate',
  TOTAL_ROAS: 'Total ROAS',
  COST_PER_TOTAL_PURCHASE: 'Cost per purchase',
  PURCHASES_TO_LINK_CLICK: 'Purchases to Link clicks',
  COST_PER_LEAD: 'Cost per lead',
  LEAD_CONVERSION_RATE: 'Lead conversion rate',  
  COST_PER_STORE_LOCATOR: 'Cost per store locator',
  COST_PER_COMPLETED_REGISTRATION: 'Cost per completed registration',
  COST_PER_INITIATED_CHECKOUT: 'Cost per initiated checkout',
  LEAD_RATE: 'Lead rate',
  RESULT_RATE: 'Result rate',
  CONVERSION_RATE: 'Conversion rate',
  PURCHASES: 'Purchases',
  PURCHASES_CONVERSION: 'Purchases conversion value',
  CPM: 'CPM',
  TRIALS_STARTED: 'Trials started',
  TRIALS_STARTED_TO_IMPRESSIONS: 'Trials started to impressions',
  COST_PER_TRIAL_STARTED: 'Cost per trial started',
  IMPRESSIONS: 'Impressions',
  REACH: 'Reach',
}

export const sentenceCase = (str: string) =>
  str[0].toUpperCase() + str.slice(1).toLowerCase()

export const yyyymmdd = (date: Date | string) => {
  if (date instanceof Date) {
    return format(date, 'yyyy-MM-dd')
  } else {
    return format(new Date(date), 'yyyy-MM-dd')
  }
}

export const metricFilterFieldKey = (str: string, currentKpi: KPI) => {
  if (str === 'Rate') {
    return KpiDisplay[currentKpi]
  }

  const result = str.replace(/([A-Z])/g, ' $1').trim()
  return result.charAt(0).toUpperCase() + result.slice(1).toLowerCase()
}

export const getInsightHeaderName = (key: string): string => {
  const fieldToHeaderMap: Record<string, string> = {
      labellift: "Label Lift",
      kpipresent: "KPI (present)",
      kpiabsent: "KPI (absent)",
      spendpresent: "Spend (present)",
      spendabsent: "Spend (absent)",
      numberofadspresent: "# of Ads (present)",
      numberofadsabsent: "# of Ads (absent)"
  };

  return fieldToHeaderMap[key.toLowerCase()] || key;
};
